import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Modal, RadioChangeEvent, Typography } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import {
  CancelReservationCancelVisits,
  CancelReservationDelegationWarning,
  CancelReservationDetailsSection,
  CancelReservationModalFooter,
  CancelReservationModalTitle,
  CancelReservationOptionsSection,
} from '../common';
import { CancelOption, CancelReservationSeriesModalProps } from '../types';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';

const SHORT_DATE_FORMAT = 'ddd, MMM D';

const isConsecutiveList = (dates: moment.Moment[]) =>
  dates.every(
    (date, index) =>
      index === dates.length - 1 || dates[index + 1].diff(date, 'days') === 1
  );

export const CancelReservationSeriesModal = ({
  open,
  afterOpenChange,
  onClose,
  onConfirm,
  reservationInProgress,
  reservationUserId,
  reservationUserName,
  reservationReserverName,
  showDelegatedWarning,
  reservationStartDate,
  reservationTimezone,
  deskName,
  buildingName,
  floorName,
  seriesDates,
  loading = false,
}: CancelReservationSeriesModalProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings.cancelReservationModal',
  });
  const [cancelOption, setCancelOption] = useState<CancelOption>('single');
  const [shouldCancelVisits, setShouldCancelVisits] = useState(false);

  const reservationDate = useMemo(
    () => reservationStartDate.clone().tz(reservationTimezone),
    [reservationStartDate, reservationTimezone]
  );

  const dates = useMemo(
    () =>
      (seriesDates?.length ? seriesDates : [reservationDate])
        .map((date) => date.clone().tz(reservationTimezone))
        .sort((left, right) => left.diff(right)),
    [reservationDate, reservationTimezone, seriesDates]
  );

  const consecutiveDatesString = useMemo(() => {
    if (!dates.length || !isConsecutiveList(dates)) {
      return null;
    }

    return `${dates[0].format(SHORT_DATE_FORMAT)} - ${dates[
      dates.length - 1
    ].format(SHORT_DATE_FORMAT)}`;
  }, [dates]);

  const formattedDates = useMemo(
    () => dates.map((date) => date.format(SHORT_DATE_FORMAT)),
    [dates]
  );

  const handleRadioButton = useCallback((event: RadioChangeEvent) => {
    setCancelOption(event.target.value as CancelOption);
  }, []);

  const handleCheckboxClick = () => {
    if (reservationInProgress && cancelOption === 'single') {
      setShouldCancelVisits(false);
      return;
    }

    setShouldCancelVisits((currentValue) => !currentValue);
  };

  const handleConfirm = () => {
    onConfirm(cancelOption, shouldCancelVisits);
  };

  return (
    <Modal
      title={
        <CancelReservationModalTitle
          reservationInProgress={reservationInProgress}
        />
      }
      open={open}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      afterOpenChange={afterOpenChange}
      onOk={handleConfirm}
      onCancel={onClose}
      modalRender={(modalNode) => (
        <ModalEventBoundary
          onClick={stopModalEvent}
          onMouseDown={stopModalEvent}
          onPointerDown={stopModalEvent}
        >
          {modalNode}
        </ModalEventBoundary>
      )}
      footer={
        <CancelReservationModalFooter
          onClose={onClose}
          handleConfirm={handleConfirm}
          loading={loading}
        />
      }
    >
      <ModalBody>
        <div>{t('confirm_cancel')}</div>

        <CancelReservationDelegationWarning
          showDelegatedWarning={!!showDelegatedWarning}
          reservationReserveeName={reservationUserName}
          reservationReserverName={reservationReserverName}
        />

        <CancelReservationDetailsSection
          reservationUserName={reservationUserName}
          deskName={deskName}
          floorName={floorName}
          buildingName={buildingName}
        >
          {loading && t('loading')}
          {!loading &&
            (consecutiveDatesString || (
              <DateColumn data-testid="dates">
                {formattedDates.map((date) => (
                  <Typography.Text key={date}>{date}</Typography.Text>
                ))}
              </DateColumn>
            ))}
        </CancelReservationDetailsSection>

        <CancelReservationOptionsSection
          selectedOption={cancelOption}
          isSeries
          reservationStartDate={reservationDate}
          reservationInProgress={reservationInProgress}
          loading={loading}
          handleRadioButton={handleRadioButton}
        />

        <CancelReservationCancelVisits
          selectedOption={cancelOption}
          reservationInProgress={reservationInProgress}
          shouldCancelVisits={shouldCancelVisits}
          handleCheckboxClick={handleCheckboxClick}
          reservationUserId={reservationUserId}
          showDelegatedWarning={showDelegatedWarning}
        />
      </ModalBody>
    </Modal>
  );
};

const DateColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalEventBoundary = styled.div``;

const stopModalEvent = (event: SyntheticEvent<HTMLDivElement>) => {
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation?.();
};
